import { inject, Injectable, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Nullable } from 'primeng/ts-helpers';
import { AuthService } from '../../../../../../core/services/auth.service';
import { LanguageService } from '../../../../../../core/services/language.service';
import { UserConfigurationService } from '../../../../../../core/services/user-configuration.service';
import { FieldLimit } from '../../../../../../shared/enums/field-limit.enum';
import { Modules } from '../../../../../../shared/enums/modules.enum';
import { AccessService } from '../../../../../../shared/services/access.service';
import { DatesService } from '../../../../../../shared/services/dates.service';
import { CreateProjectCommand } from '../../../contract/create-project-command.contract';
import { UpdateProjectCommand } from '../../../contract/update-project-command.contract';
import { ViewProject } from '../../../contract/view-project.contract';
import { ProjectType } from '../../../enums/project-type.enum';

export interface ProjectForm {
  projectData: FormGroup<ProjectDataForm>;
  projectAddress: FormGroup<ProjectAddressForm>;
}

export interface ProjectDataForm {
  projectName: FormControl<Nullable<string>>;
  projectStart: FormControl<Nullable<Date>>;
  projectEnd: FormControl<Nullable<Date>>;
  projectType: FormControl<Nullable<ProjectType>>;
  projectCostCenter: FormControl<Nullable<string>>;
  description: FormControl<Nullable<string>>;
  organisationId: FormControl<Nullable<string>>;
}

export interface ProjectAddressForm {
  street: FormControl<Nullable<string>>;
  streetNumber: FormControl<Nullable<string>>;
  postalCode: FormControl<Nullable<string>>;
  city: FormControl<Nullable<string>>;
  createGeofence: FormControl<Nullable<boolean>>;
  lat: FormControl<Nullable<number>>;
  lon: FormControl<Nullable<number>>;
}

@Injectable()
export class ProjectAddEditDialogFormController {
  private readonly languageService = inject(LanguageService);
  private readonly authService = inject(AuthService);
  private readonly accessService = inject(AccessService);
  private readonly userConfigurationService = inject(UserConfigurationService);

  private form!: FormGroup<ProjectForm>;
  private readonly $project = signal<Nullable<ViewProject>>(null);

  readonly projectNameErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.NAME }),
  };
  readonly projectCostCenterErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.COST_CENTER }),
  };
  readonly organisationErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
  };
  readonly projectStartErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
  };
  readonly projectDateRangeErrorMessages = {
    invalidProjectDateRange: this.translate('modules.locations.project.create.form.dateRange.invalid'),
  };
  readonly projectTypeErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
  };
  readonly streetErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.STREET }),
  };
  readonly streetNumberErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.STREET_NUMBER }),
  };
  readonly postalCodeErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.POSTAL_CODE }),
  };
  readonly cityErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.CITY }),
  };
  readonly descriptionErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.MEDIUM_DESCRIPTION }),
  };

  buildForm(project?: ViewProject): FormGroup<ProjectForm> {
    this.$project.set(project ?? null);
    this.form = new FormGroup<ProjectForm>({
      projectData: new FormGroup<ProjectDataForm>(
        {
          projectName: new FormControl(project?.projectName || null, [
            Validators.required,
            Validators.maxLength(FieldLimit.NAME),
          ]),
          projectStart: new FormControl(project?.projectStart ?? new Date(), [Validators.required]),
          projectEnd: new FormControl(project?.projectEnd ?? null),
          projectType: new FormControl(project?.projectType ?? null, [Validators.required]),
          projectCostCenter: new FormControl(project?.projectCostCenter || null, [
            Validators.maxLength(FieldLimit.COST_CENTER),
          ]),
          description: new FormControl(project?.description || null, [
            Validators.maxLength(FieldLimit.MEDIUM_DESCRIPTION),
          ]),
          organisationId: new FormControl(project?.organisationId || null, [Validators.required]),
        },
        { validators: this.projectDateRangeValidator() },
      ),
      projectAddress: new FormGroup<ProjectAddressForm>({
        street: new FormControl(project?.projectAddress?.street || null, [Validators.maxLength(FieldLimit.STREET)]),
        streetNumber: new FormControl(project?.projectAddress?.streetNumber || null, [
          Validators.maxLength(FieldLimit.STREET_NUMBER),
        ]),
        postalCode: new FormControl(project?.projectAddress?.postalCode || null, [
          Validators.maxLength(FieldLimit.POSTAL_CODE),
        ]),
        city: new FormControl(project?.projectAddress?.city || null, [Validators.maxLength(FieldLimit.CITY)]),
        createGeofence: new FormControl(this.shouldCreateGeofenceByDefault()),
        lat: new FormControl(project?.location?.lat ?? null),
        lon: new FormControl(project?.location?.lon ?? null),
      }),
    });

    return this.form;
  }

  getCreateCommand(): CreateProjectCommand {
    const value = this.form.getRawValue();

    return {
      projectName: value.projectData.projectName || '',
      projectType: value.projectData.projectType || ProjectType.FIX,
      projectCostCenter: value.projectData.projectCostCenter || '',
      projectStart: value.projectData.projectStart
        ? DatesService.ospDateTimeAtStartOfDay(value.projectData.projectStart)
        : null,
      projectEnd: value.projectData.projectEnd
        ? DatesService.ospDateTimeAtEndOfDay(value.projectData.projectEnd)
        : null,
      description: value.projectData.description || '',
      organisationId: value.projectData.organisationId || '',
      projectAddress: {
        street: value.projectAddress.street || '',
        streetNumber: value.projectAddress.streetNumber || '',
        postalCode: value.projectAddress.postalCode || '',
        city: value.projectAddress.city || '',
      },
      createGeofence: this.accessService.canManageGeofence() && !!value.projectAddress.createGeofence,
      isDispositionGeofence: this.authService.hasModule(Modules.AUTOMATED_TRANSFERS),
      latitude: value.projectAddress.lat || 0,
      longitude: value.projectAddress.lon || 0,
    };
  }

  getUpdateCommand(): UpdateProjectCommand {
    const project = this.$project();
    if (!project) throw new Error('Project is required for update');

    const value = this.form.getRawValue();

    return {
      projectId: project.projectId,
      projectName: value.projectData.projectName || '',
      projectType: value.projectData.projectType || ProjectType.FIX,
      projectCostCenter: value.projectData.projectCostCenter || '',
      projectStart: value.projectData.projectStart
        ? DatesService.ospDateTimeAtStartOfDay(value.projectData.projectStart)
        : null,
      projectEnd: value.projectData.projectEnd
        ? DatesService.ospDateTimeAtEndOfDay(value.projectData.projectEnd)
        : null,
      description: value.projectData.description || '',
      organisationId: value.projectData.organisationId || '',
      projectAddress: {
        street: value.projectAddress.street || '',
        streetNumber: value.projectAddress.streetNumber || '',
        postalCode: value.projectAddress.postalCode || '',
        city: value.projectAddress.city || '',
      },
      latitude: value.projectAddress.lat || 0,
      longitude: value.projectAddress.lon || 0,
    };
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }

  private shouldCreateGeofenceByDefault(): boolean {
    return (
      this.accessService.canManageGeofence() && this.userConfigurationService.getProjectAddCreateGeofenceConfiguration()
    );
  }

  private projectDateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const projectData = control as FormGroup<ProjectDataForm>;
      const startDate = projectData.controls.projectStart.value;
      const endDate = projectData.controls.projectEnd.value;

      if (!startDate || !endDate) {
        return null;
      }

      if (startDate.getTime() > endDate.getTime()) {
        return { invalidProjectDateRange: true };
      }

      return null;
    };
  }
}
